import { Icon, Image } from '@alto-avios/alto-ui';
import { FC, useCallback, useState } from 'react';
import { useVariableValue } from '@devcycle/react-client-sdk';

import { HEADER } from '../../consts';
import { pushGTMEvent, pushtoGTM } from '../../utilities';
import { Button } from '../Button';
import { AuthProps } from '../Header';
import { MobileMenuButton } from '../MobileMenuButton';
import { useOverlayContext } from '../Overlay/useOverlayContext';
import { HeaderContent, LocaleContent, MarketContent, OpCoContent, useRequiredContent } from '../services/content';
import { ModalShell } from '../Modal/ModalShell';
import { PreferencesSelector } from '../PreferencesSelector';
import { useUIString } from '../services/content/hooks/useUIString';

import styles from './MobileUserDetails.module.scss';

export type MobileUserDetailsProps = AuthProps & {
  isLoggedIn: boolean;
  showHelpCentre?: boolean;
};

export const MobileUserDetails: FC<MobileUserDetailsProps> = ({ isLoggedIn, loginProps, logoutProps, showHelpCentre }) => {
  const hasMultiOpco = useVariableValue('multi-opco-login', false);
  const [isModalOpen, setModalOpen] = useState(false);
  const overlayContext = useOverlayContext();

  const {
    data: { account, helpCentre, registerLabel, registerUrl, signInLabel },
  } = useRequiredContent(HeaderContent);
  const {
    data: { id: opcoId },
  } = useRequiredContent(OpCoContent);
  const {
    data: { shortLabel, image },
  } = useRequiredContent(MarketContent);
  const {
    data: { label: locale },
  } = useRequiredContent(LocaleContent);

  const preferencesModalOpenLinkAriaLabel = useUIString('preferencesModalOpenLinkAriaLabel');
  const preferencesModalTitle = useUIString('preferencesModalTitle');
  const dialogDismissAriaLabel = useUIString('dialogDismissAriaLabel');

  const handleLoginPress = useCallback(() => {
    pushGTMEvent(HEADER, signInLabel);
    loginProps.onClick?.(opcoId);
    overlayContext?.close();
  }, [overlayContext, loginProps, signInLabel, opcoId]);

  const handleLogoutPress = useCallback(() => {
    pushGTMEvent(HEADER, account.logout.label);
    logoutProps.onClick?.(opcoId);
    overlayContext?.close();
  }, [overlayContext, logoutProps, account.logout.label, opcoId]);

  const handleHelpCentrePress = useCallback(() => {
    pushGTMEvent(HEADER, helpCentre.label);
    overlayContext?.close();
  }, [overlayContext, helpCentre.label]);

  const handleRegisterPress = useCallback(() => {
    pushGTMEvent(HEADER, registerLabel);
    overlayContext?.close();
  }, [overlayContext, registerLabel]);

  return (
    <>
      {hasMultiOpco && (
        <ModalShell
          heading={preferencesModalTitle}
          isOpen={isModalOpen}
          onClose={() => (pushtoGTM('ctaEvent', 'modal - preferences', 'cta', 'close modal'), setModalOpen(false))}
          ariaLabel={preferencesModalOpenLinkAriaLabel}
          closeButtonAriaLabel={dialogDismissAriaLabel}
          size="small"
        >
          <PreferencesSelector />
        </ModalShell>
      )}
      <ul className={styles.mobileUserDetails}>
        {hasMultiOpco && (
          <li>
            <MobileMenuButton
              emphasis="neutral"
              onPress={() => (setModalOpen(true), pushGTMEvent(HEADER, `${shortLabel} - ${locale}`))}
              aria-label={preferencesModalOpenLinkAriaLabel}
              hideChevron
            >
              {image && <Image src={image.url} alt={image.title} width={16} />}
              {shortLabel} - {locale}
            </MobileMenuButton>
          </li>
        )}
        <li>
          {showHelpCentre && (
            <MobileMenuButton href={helpCentre.url} emphasis="neutral" onPress={handleHelpCentrePress} aria-label={helpCentre.label}>
              <Icon iconName="circle-question" iconPrefix="fal" /> {helpCentre.label}
            </MobileMenuButton>
          )}
        </li>
        {isLoggedIn ? (
          <li>
            <MobileMenuButton
              href={logoutProps.href}
              emphasis="accent"
              onPress={handleLogoutPress}
              hideChevron
              disableRouting
              aria-label={account.logout.label}
            >
              <Icon iconName={account.logout.icon} /> {account.logout.label}
            </MobileMenuButton>
          </li>
        ) : (
          <ul>
            <li>
              <Button href={loginProps.href} emphasis="secondary" onPress={handleLoginPress} disableRouting>
                {signInLabel}
              </Button>
            </li>
            <li>
              <Button href={registerUrl} onPress={handleRegisterPress} disableRouting>
                {registerLabel}
              </Button>
            </li>
          </ul>
        )}
      </ul>
    </>
  );
};
